/**
 * Team Bios API Slice
 *
 * RTK Query API slice for Team Bios feature endpoints.
 * Handles config management, member bio CRUD, and public display.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  TeamBioConfig,
  TeamBioConfigUpdate,
  TeamMemberBio,
  TeamMemberBioUpdate,
  TeamMemberBioListResponse,
  PublicTeamBiosResponse,
} from '../types/teamBios';

export const teamBiosApi = createApi({
  reducerPath: 'teamBiosApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['TeamBioConfig', 'TeamMemberBio'],
  endpoints: builder => ({
    // ========================================================================
    // Config Endpoints
    // ========================================================================

    /**
     * Get team bio config for a studio (auto-creates if doesn't exist)
     */
    getTeamBioConfig: builder.query<TeamBioConfig, string>({
      query: studioId => `/studios/${studioId}/team-bios/config/`,
      providesTags: ['TeamBioConfig'],
    }),

    /**
     * Update team bio config
     */
    updateTeamBioConfig: builder.mutation<
      TeamBioConfig,
      { studioId: string; data: TeamBioConfigUpdate }
    >({
      query: ({ studioId, data }) => ({
        url: `/studios/${studioId}/team-bios/config/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['TeamBioConfig'],
    }),

    // ========================================================================
    // Member Bio Endpoints
    // ========================================================================

    /**
     * Get team member bios list (auto-creates for active members)
     */
    getTeamMemberBios: builder.query<
      TeamMemberBioListResponse,
      { studioId: string; params?: Record<string, any> }
    >({
      query: ({ studioId, params }) => ({
        url: `/studios/${studioId}/team-bios/members/`,
        params,
      }),
      providesTags: ['TeamMemberBio'],
    }),

    /**
     * Get single team member bio detail
     */
    getTeamMemberBio: builder.query<
      TeamMemberBio,
      { studioId: string; membershipId: string }
    >({
      query: ({ studioId, membershipId }) =>
        `/studios/${studioId}/team-bios/members/${membershipId}/`,
      providesTags: (result, error, { membershipId }) => [
        { type: 'TeamMemberBio', id: membershipId },
      ],
    }),

    /**
     * Update team member bio
     */
    updateTeamMemberBio: builder.mutation<
      TeamMemberBio,
      { studioId: string; membershipId: string; data: TeamMemberBioUpdate }
    >({
      query: ({ studioId, membershipId, data }) => ({
        url: `/studios/${studioId}/team-bios/members/${membershipId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { membershipId }) => [
        { type: 'TeamMemberBio', id: membershipId },
        'TeamMemberBio',
      ],
    }),

    /**
     * Upload custom photo for team member bio
     */
    uploadTeamMemberPhoto: builder.mutation<
      TeamMemberBio,
      { studioId: string; membershipId: string; photo: File }
    >({
      query: ({ studioId, membershipId, photo }) => {
        const formData = new FormData();
        formData.append('custom_photo', photo);
        return {
          url: `/studios/${studioId}/team-bios/members/${membershipId}/photo/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { membershipId }) => [
        { type: 'TeamMemberBio', id: membershipId },
        'TeamMemberBio',
      ],
    }),

    // ========================================================================
    // Public Endpoint
    // ========================================================================

    /**
     * Get public team bios (no auth required)
     */
    getPublicTeamBios: builder.query<PublicTeamBiosResponse, string>({
      query: studioSlugOrId => `/public/team-bios/${studioSlugOrId}/`,
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetTeamBioConfigQuery,
  useUpdateTeamBioConfigMutation,
  useGetTeamMemberBiosQuery,
  useGetTeamMemberBioQuery,
  useUpdateTeamMemberBioMutation,
  useUploadTeamMemberPhotoMutation,
  useGetPublicTeamBiosQuery,
} = teamBiosApi;
